import React, { useId } from 'react';
import { Flex } from '@indeed/ifl-components';

import { useRadioCardGroupProps } from '../ResumeRadioCard/RadioCardGroupContext';
import { RadioCardProvider } from '../ResumeRadioCard/RadioCardContext';
import {
    RadioCardCommonProps,
    RadioCardInputProps,
    RadioCardVariants,
} from '../ResumeRadioCard/types';

export interface RadioCardProps extends RadioCardCommonProps, RadioCardInputProps {
    children: React.ReactNode;
    id?: string;
    checked?: boolean;
    expanded?: boolean;
    variant?: RadioCardVariants;
    inputProps?: Omit<
        React.ComponentPropsWithoutRef<'input'>,
        | 'type'
        | 'id'
        | 'name'
        | 'value'
        | 'checked'
        | 'onChange'
        | 'aria-controls'
        | 'aria-expanded'
        | 'required'
    >;
    subtitle?: string;
}

export const ScoutRadioCard: React.FunctionComponent<RadioCardProps> = ({
    id: idProp,
    name,
    checked: checkedProp,
    onChange,
    value,
    expanded,
    required,
    variant: variantProp,
    sx,
    'data-testid': dataTestId = 'RadioCard',
    children,
    inputProps,
    ...props
}) => {
    const {
        value: radioCardGroupValue,
        name: radioCardGroupName,
        onChange: radioCardGroupOnChange,
        variant: radioCardGroupVariant,
    } = useRadioCardGroupProps();
    const generatedId = useId();
    const id = idProp || `scout-radio-${generatedId}`;
    const inputId = `${id}-input`;
    const expandableRegionId = `${id}-expandable-region`;
    const checked =
        checkedProp ??
        (typeof radioCardGroupValue !== 'undefined' && radioCardGroupValue === value);
    const variant = variantProp || radioCardGroupVariant || 'check';

    const [focused, setFocused] = React.useState(false);

    return (
        <RadioCardProvider
            value={{ variant, inputId, expandableRegionId, expanded, 'data-testid': dataTestId }}
        >
            <Flex
                id={id}
                data-testid={dataTestId}
                data-checked={checked}
                data-expanded={expanded}
                data-focused={focused}
                sx={{
                    flexDirection: 'column',
                    cursor: 'pointer',
                    '& > label': {
                        cursor: 'inherit',
                        m: -4,
                        p: 4,
                    },
                    borderRadius: 'md',
                    p: 4,
                    bg: 'background.action',
                    border: '1px solid',
                    borderColor: 'border.weak',
                    transition: ({ transitions }) => `all ${transitions?.default}`,
                    '@media (prefers-reduced-motion)': {
                        transitionProperty: 'none',
                    },
                    '&[data-checked=true]': { borderColor: 'utility.selection' },
                    outlineWidth: 0,
                    outlineColor: 'transparent',
                    outlineStyle: 'solid',
                    outlineOffset: '-1px',
                    '&[data-focused=true]': {
                        outlineWidth: '1px',
                        outlineOffset: '0.125rem',
                        outlineColor: 'utility.focus',
                    },
                    'input[type=radio]': {
                        appearance: 'none',
                        p: 0,
                        m: 0,
                    },
                    ...sx,
                }}
                {...props}
            >
                <input
                    data-testid={`${dataTestId}-input`}
                    {...inputProps}
                    id={inputId}
                    type="radio"
                    value={value}
                    name={name || radioCardGroupName}
                    checked={checked}
                    required={required}
                    {...(typeof expanded === 'boolean' && {
                        'aria-controls': expandableRegionId,
                    })}
                    onFocus={(...args) => {
                        setFocused(true);
                        inputProps?.onFocus?.(...args);
                    }}
                    onBlur={(...args) => {
                        setFocused(false);
                        inputProps?.onBlur?.(...args);
                    }}
                    onChange={(...args) => {
                        onChange?.(...args);
                        radioCardGroupOnChange?.(...args);
                    }}
                />
                {children}
            </Flex>
        </RadioCardProvider>
    );
};
